/**
 * ============================================================================
 * ADMIN PAYMENTS PAGE
 * ============================================================================
 * Same structure and UX as Admin Finance: hero, stats bento, filters,
 * payments list, pagination.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useAdminStore } from '../../store';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import AdminLayout from '../../components/admin/AdminLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Textarea from '../../components/common/Textarea';
import '../../styles/landing.css';
import '../../styles/animations.css';
import '../../styles/global.css';

/** Normalize backend payment (snake_case) for display */
function normalizePayment(p) {
  if (!p) return null;
  return {
    ...p,
    transactionId: p.transaction_id ?? p.transactionId,
    createdAt: p.created_at ?? p.createdAt,
    paymentMethod: p.payment_method ?? p.paymentMethod ?? p.method,
    paymentReference: p.payment_reference ?? p.paymentReference,
  };
}

const AdminPayments = () => {
  const navigate = useNavigate();
  const {
    listAllPayments,
    getPaymentDetails,
    retryPayment,
    refundPayment,
    loading,
  } = useAdmin();
  const { filters, setFilters, pagination, setPagination } = useAdminStore();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState(filters?.search || '');
  const [statusFilter, setStatusFilter] = useState(filters?.status ?? '');
  const [methodFilter, setMethodFilter] = useState(filters?.payment_method ?? '');
  const [isLoading, setIsLoading] = useState(true);
  const [statsInView, setStatsInView] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({});
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const response = await listAllPayments({
          status: statusFilter || undefined,
          payment_method: methodFilter || undefined,
          page: pagination?.page || 1,
          limit: pagination?.pageSize || 20,
        });
        const list = Array.isArray(response?.data) ? response.data : [];
        setPaymentsList(list);
        if (response?.pagination) setPaymentsPagination(response.pagination);
        if (response?.summary) setSummary(response.summary);
        if (response?.pagination) {
          setPagination({
            ...pagination,
            total: response.pagination.total,
            totalPages: response.pagination.pages ?? Math.ceil((response.pagination.total ?? 0) / (response.pagination.limit ?? 20)),
          });
        }
      } catch (error) {
        showError(
          error.message ||
            (language === 'fr' ? 'Échec du chargement des paiements' : 'Failed to load payments')
        );
        console.error('Load payments error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    listAllPayments,
    statusFilter,
    methodFilter,
    showError,
    language,
    pagination?.page,
    pagination?.pageSize,
  ]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchQuery });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    if (key === 'status') setStatusFilter(value);
    if (key === 'payment_method') setMethodFilter(value);
    setPagination({ ...pagination, page: 1 });
  };

  const clearFilters = () => {
    setFilters({ search: null, status: null, payment_method: null });
    setSearchQuery('');
    setStatusFilter('');
    setMethodFilter('');
    setPagination({ ...pagination, page: 1 });
  };

  const handleAction = async () => {
    if (!selectedPayment) return;
    try {
      if (actionType === 'retry') {
        await retryPayment(selectedPayment.id, actionData);
        showSuccess(language === 'fr' ? 'Paiement relancé!' : 'Payment retried!');
      } else if (actionType === 'refund') {
        if (!actionData.reason || actionData.reason.length < 10) {
          showError(
            language === 'fr'
              ? 'La raison du remboursement doit contenir au moins 10 caractères'
              : 'Refund reason must be at least 10 characters'
          );
          return;
        }
        await refundPayment(selectedPayment.id, actionData);
        showSuccess(language === 'fr' ? 'Remboursement effectué!' : 'Refund completed!');
      }
      setShowActionModal(false);
      setSelectedPayment(null);
      setActionData({});
      const response = await listAllPayments({
        status: statusFilter || undefined,
        payment_method: methodFilter || undefined,
        page: 1,
        limit: pagination?.pageSize || 20,
      });
      setPaymentsList(Array.isArray(response?.data) ? response.data : []);
      if (response?.pagination) setPaymentsPagination(response.pagination);
      if (response?.summary) setSummary(response.summary);
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? "Échec de l'action" : 'Failed to perform action')
      );
    }
  };

  const getPaymentIcon = (method) => {
    const icons = {
      card: '💳',
      mtn_mobile_money: '📱',
      mpesa: '📱',
      orange_money: '🍊',
      bank_transfer: '🏦',
      wallet: '💰',
    };
    return icons[method] || '💰';
  };

  const getPaymentMethodName = (method) => {
    const names = {
      card: language === 'fr' ? 'Carte' : 'Card',
      mtn_mobile_money: 'MTN Mobile Money',
      orange_money: 'Orange Money',
      mpesa: 'M-Pesa',
      bank_transfer: language === 'fr' ? 'Virement' : 'Bank Transfer',
      wallet: language === 'fr' ? 'Portefeuille' : 'Wallet',
    };
    return names[method] || method;
  };

  const list = Array.isArray(paymentsList) ? paymentsList : [];
  const stats = {
    total: paymentsPagination?.total ?? summary?.total_payments ?? list.length,
    pending: summary?.pending ?? list.filter((p) => p.status === 'pending' || p.status === 'processing').length,
    completed: summary?.completed ?? list.filter((p) => p.status === 'completed').length,
    failed: summary?.failed ?? list.filter((p) => p.status === 'failed').length,
    totalAmount: parseFloat(summary?.total_amount) ?? list.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
  };

  const statusOptions = [
    { value: '', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'pending', label: language === 'fr' ? 'En attente' : 'Pending' },
    { value: 'processing', label: language === 'fr' ? 'En traitement' : 'Processing' },
    { value: 'completed', label: language === 'fr' ? 'Complété' : 'Completed' },
    { value: 'failed', label: language === 'fr' ? 'Échoué' : 'Failed' },
    { value: 'refunded', label: language === 'fr' ? 'Remboursé' : 'Refunded' },
    { value: 'cancelled', label: language === 'fr' ? 'Annulé' : 'Cancelled' },
  ];

  const methodOptions = [
    { value: '', label: language === 'fr' ? 'Toutes les méthodes' : 'All Methods' },
    { value: 'card', label: language === 'fr' ? 'Carte' : 'Card' },
    { value: 'mpesa', label: 'M-Pesa' },
    { value: 'mtn_mobile_money', label: 'MTN Mobile Money' },
    { value: 'orange_money', label: 'Orange Money' },
    { value: 'bank_transfer', label: language === 'fr' ? 'Virement' : 'Bank Transfer' },
    { value: 'wallet', label: language === 'fr' ? 'Portefeuille' : 'Wallet' },
  ];

  const totalFromPagination = paymentsPagination?.total ?? list.length;
  const showPagination = totalFromPagination > (pagination?.pageSize ?? 20);

  if (isLoading || loading) {
    return (
      <AdminLayout hideFooter>
        <div className="transactions-wt transactions-wt-loading">
          <div className="dashboard-wt-hero-inner">
            <Spinner size="lg" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement des paiements...' : 'Loading payments...'}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="transactions-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />

          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              💳 {language === 'fr' ? 'Gestion des Paiements' : 'Payment Management'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Gérez et surveillez tous les paiements du système'
                : 'Manage and monitor all payments in the system'}
            </p>
            <button
              type="button"
              className="dashboard-wt-btn-primary dashboard-wt-btn-large mt-6"
              onClick={() => navigate('/admin/dashboard')}
            >
              <span className="dashboard-wt-btn-icon">←</span>
              {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
            </button>
          </div>
        </section>

        {/* Stats - Bento */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
              {[
                {
                  icon: '📦',
                  label: language === 'fr' ? 'Total' : 'Total',
                  value: stats.total,
                },
                {
                  icon: '⏳',
                  label: language === 'fr' ? 'En attente' : 'Pending',
                  value: stats.pending,
                },
                {
                  icon: '✅',
                  label: language === 'fr' ? 'Complétés' : 'Completed',
                  value: stats.completed,
                },
                {
                  icon: '💰',
                  label: language === 'fr' ? 'Montant (page)' : 'Amount (page)',
                  value: formatCurrency(stats.totalAmount),
                },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="dashboard-wt-stat-card"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="dashboard-wt-stat-icon">
                    <span>{stat.icon}</span>
                  </div>
                  <div className="dashboard-wt-stat-content">
                    <div className="dashboard-wt-stat-value">{stat.value}</div>
                    <div className="dashboard-wt-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filters & List */}
        <section className="dashboard-wt-section dashboard-wt-section-alt">
          <div className="dashboard-wt-section-inner">
            <div className="transactions-wt-filters dashboard-wt-card">
              <div className="dashboard-wt-card-header">
                <h2 className="dashboard-wt-card-title">
                  {language === 'fr' ? 'Filtres' : 'Filters'}
                </h2>
              </div>
              <div className="dashboard-wt-card-content">
                <form onSubmit={handleSearch} className="transactions-wt-form">
                  <div className="transactions-wt-form-row">
                    <div className="transactions-wt-form-search">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                          language === 'fr'
                            ? 'Référence, ID...'
                            : 'Reference, ID...'
                        }
                        className="transactions-wt-input"
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={statusFilter || ''}
                        onChange={(e) =>
                          handleFilterChange('status', e.target.value || null)
                        }
                        options={statusOptions}
                      />
                    </div>
                    <div className="transactions-wt-form-select">
                      <Select
                        value={methodFilter || ''}
                        onChange={(e) =>
                          handleFilterChange('payment_method', e.target.value || null)
                        }
                        options={methodOptions}
                      />
                    </div>
                    <button type="submit" className="dashboard-wt-btn-primary">
                      {language === 'fr' ? 'Rechercher' : 'Search'}
                    </button>
                    {(filters?.search || filters?.status || filters?.payment_method) && (
                      <button
                        type="button"
                        className="dashboard-wt-btn-secondary"
                        onClick={clearFilters}
                      >
                        {language === 'fr' ? 'Effacer' : 'Clear'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {list.length === 0 ? (
              <div className="dashboard-wt-card dashboard-wt-empty">
                <div className="dashboard-wt-empty-icon">💳</div>
                <h3 className="dashboard-wt-empty-title">
                  {language === 'fr' ? 'Aucun paiement' : 'No payments found'}
                </h3>
                <p className="dashboard-wt-empty-desc">
                  {language === 'fr'
                    ? 'Aucun paiement ne correspond à vos filtres'
                    : 'No payments match your filters'}
                </p>
                <button
                  type="button"
                  className="dashboard-wt-btn-secondary dashboard-wt-btn-large"
                  onClick={clearFilters}
                >
                  {language === 'fr' ? 'Effacer les filtres' : 'Clear Filters'}
                </button>
              </div>
            ) : (
              <>
                <div className="dashboard-wt-transactions transactions-wt-list">
                  {list.map((payment, index) => {
                    const p = normalizePayment(payment);
                    return (
                      <div
                        key={payment.id}
                        className="dashboard-wt-transaction-wrapper dashboard-wt-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="dashboard-wt-card-content">
                          <div className="flex flex-wrap items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/80 to-green-600/80 flex items-center justify-center text-xl shadow-lg flex-shrink-0">
                              {getPaymentIcon(p.paymentMethod)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-[#e5e5e5]">
                                  #{payment.id?.slice(0, 8) ?? 'N/A'}
                                </h3>
                                <span
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    payment.status === 'completed'
                                      ? 'bg-green-500/20 text-green-400'
                                      : payment.status === 'pending' || payment.status === 'processing'
                                      ? 'bg-amber-500/20 text-amber-400'
                                      : payment.status === 'failed'
                                      ? 'bg-red-500/20 text-red-400'
                                      : 'bg-[#2a2a2a] text-[#a3a3a3]'
                                  }`}
                                >
                                  {(payment.status || '').toUpperCase()}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#737373]">
                                <span>
                                  {formatCurrency(p.amount ?? 0, p.currency ?? 'XAF')}
                                </span>
                                <span>·</span>
                                <span>
                                  {getPaymentMethodName(p.paymentMethod)}
                                </span>
                                {p.transactionId && (
                                  <>
                                    <span>·</span>
                                    <span>
                                      {language === 'fr' ? 'Txn' : 'Txn'} #{p.transactionId?.slice(0, 8)}
                                    </span>
                                  </>
                                )}
                                <span>·</span>
                                <span>{p.createdAt && formatDate(p.createdAt)}</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                              <button
                                type="button"
                                className="dashboard-wt-btn-secondary"
                                onClick={() => navigate(`/admin/payments/${payment.id}`)}
                              >
                                {language === 'fr' ? 'Détails' : 'Details'}
                              </button>
                              {payment.status === 'failed' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-primary"
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setActionType('retry');
                                    setActionData({});
                                    setShowActionModal(true);
                                  }}
                                >
                                  {language === 'fr' ? 'Réessayer' : 'Retry'}
                                </button>
                              )}
                              {payment.status === 'completed' && (
                                <button
                                  type="button"
                                  className="dashboard-wt-btn-secondary border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => {
                                    setSelectedPayment(payment);
                                    setActionType('refund');
                                    setActionData({});
                                    setShowActionModal(true);
                                  }}
                                >
                                  {language === 'fr' ? 'Rembourser' : 'Refund'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showPagination && (
                  <div className="transactions-wt-pagination dashboard-wt-card">
                    <div className="transactions-wt-pagination-inner">
                      <span className="transactions-wt-pagination-text">
                        {language === 'fr' ? 'Affichage' : 'Showing'}{' '}
                        {(pagination.page - 1) * (pagination.pageSize || 20) + 1}{' '}
                        {language === 'fr' ? 'à' : 'to'}{' '}
                        {Math.min(
                          pagination.page * (pagination.pageSize || 20),
                          totalFromPagination
                        )}{' '}
                        {language === 'fr' ? 'sur' : 'of'}{' '}
                        {totalFromPagination}
                      </span>
                      <div className="transactions-wt-pagination-btns">
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page - 1 })
                          }
                          disabled={pagination.page <= 1}
                        >
                          {language === 'fr' ? 'Précédent' : 'Previous'}
                        </button>
                        <button
                          type="button"
                          className="dashboard-wt-btn-secondary"
                          onClick={() =>
                            setPagination({ ...pagination, page: pagination.page + 1 })
                          }
                          disabled={
                            pagination.page * (pagination.pageSize || 20) >=
                            totalFromPagination
                          }
                        >
                          {language === 'fr' ? 'Suivant' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* Action Modal */}
      <Modal
        isOpen={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedPayment(null);
          setActionData({});
        }}
        title={
          actionType === 'retry'
            ? language === 'fr' ? 'Réessayer le Paiement' : 'Retry Payment'
            : language === 'fr' ? 'Rembourser le Paiement' : 'Refund Payment'
        }
      >
        {selectedPayment && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'fr' ? 'Paiement' : 'Payment'}:{' '}
                {formatCurrency(selectedPayment.amount || 0, selectedPayment.currency || 'XAF')}
              </p>
            </div>
            {actionType === 'refund' && (
              <Textarea
                label={
                  language === 'fr'
                    ? 'Raison du remboursement (requis, min 10 caractères)'
                    : 'Refund reason (required, min 10 chars)'
                }
                value={actionData.reason || ''}
                onChange={(e) =>
                  setActionData((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder={
                  language === 'fr'
                    ? 'Expliquez la raison du remboursement...'
                    : 'Explain the reason for refund...'
                }
                rows={4}
              />
            )}
            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedPayment(null);
                  setActionData({});
                }}
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                variant="gradient"
                fullWidth
                onClick={handleAction}
                disabled={actionType === 'refund' && (!actionData.reason || actionData.reason.length < 10)}
                className={
                  actionType === 'retry'
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600'
                }
              >
                {actionType === 'retry'
                  ? language === 'fr' ? 'Réessayer' : 'Retry'
                  : language === 'fr' ? 'Rembourser' : 'Refund'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default AdminPayments;
